import React from 'react';
import { Box, Link } from '@mui/material';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { english } from 'src/i18n/language';

// The HPO release behind HPO-derived results, shown where those results are
// (TGCLOUD-1846, decision 6). The HPO licence asks that any HPO file displayed
// publicly carries its date or version; Settings → Terminology carries the full
// statement, and this is the per-surface line for Clinical Insights and
// Suggested Clinical Terms.
//
// `release` comes from the response that produced the results — the release
// that actually answered — not from a separate lookup:
//   { ontology: '2026-09-01', annotations: '2026-09-01',
//     annotationFile: '2026-09-02' }

/**
 * The footer text, or null when there is nothing trustworthy to say. `t` is
 * the caller's; English when none is given.
 */
export function hpoReleaseText(release, t = english) {
  if (!release) return null;
  const ontology = release.ontology || null;
  const annotations = release.annotations || null;
  const main = ontology || annotations;
  if (!main) return null;
  const parts = [
    t('Uses the Human Phenotype Ontology, release {{release}}', {
      release: main
    })
  ];
  if (ontology && annotations && annotations !== ontology) {
    parts.push(t('annotations release {{release}}', { release: annotations }));
  }
  if (release.annotationFile && release.annotationFile !== main) {
    parts.push(t('annotation file {{file}}', { file: release.annotationFile }));
  }
  return `${parts.join(' · ')}.`;
}

export default function HpoReleaseNote({ release, sx }) {
  const t = useAutoTranslation();

  const text = hpoReleaseText(release, t);
  if (!text) return null;
  return (
    <Box
      data-testid="hpo-release-note"
      sx={{ fontSize: '.68rem', color: 'text.secondary', px: 1, ...sx }}
    >
      {text}{' '}
      <Link
        href="https://hpo.jax.org/"
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
      >
        {/* i18n-ignore: a web address */}
        hpo.jax.org
      </Link>
    </Box>
  );
}
